import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";

const emptyArticle = { title: "", slug: "", excerpt: "", body: "", tags: "", status: "draft" };

export const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

export default function ArticlesManager({ api }) {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyArticle);

  const load = useCallback(async () => {
    const data = await api("/admin/articles");
    setArticles(data.items);
  }, [api]);

  useEffect(() => {
    load().catch((e) => toast.error(e.message));
  }, [load]);

  const startNew = () => {
    setDraft(emptyArticle);
    setEditing("new");
  };

  const startEdit = (a) => {
    setDraft({ title: a.title, slug: a.slug, excerpt: a.excerpt, body: a.body, tags: (a.tags || []).join(", "), status: a.status });
    setEditing(a.id);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: draft.title,
      slug: draft.slug || slugify(draft.title),
      excerpt: draft.excerpt,
      body: draft.body,
      tags: draft.tags.split(",").map((x) => x.trim()).filter(Boolean),
      status: draft.status,
    };
    try {
      if (editing === "new") {
        await api("/admin/articles", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Article created");
      } else {
        await api(`/admin/articles/${editing}`, { method: "PUT", body: JSON.stringify(payload) });
        toast.success("Article saved");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (id) => {
    try {
      await api(`/admin/articles/${id}`, { method: "DELETE" });
      toast.success("Article deleted");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";

  if (editing) {
    return (
      <section className="mt-8" data-testid="article-editor">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-emerald-700" />
          <h2 className="font-display text-lg font-bold text-forest-deep">{editing === "new" ? "New article" : "Edit article"}</h2>
        </div>
        <form onSubmit={save} className="mt-4 space-y-3 rounded-2xl border border-emerald-950/10 bg-white p-5" data-testid="article-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-forest-deep/60">Title</label>
            <input required maxLength={200} value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className={`${inputCls} mt-1`} data-testid="article-input-title" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-forest-deep/60">Slug (URL)</label>
              <input
                value={draft.slug}
                onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))}
                onBlur={() => setDraft((d) => ({ ...d, slug: d.slug ? slugify(d.slug) : slugify(d.title) }))}
                placeholder="auto-from-title"
                className={`${inputCls} mt-1 font-mono text-xs`}
                data-testid="article-input-slug"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-forest-deep/60">Tags (comma separated)</label>
              <input value={draft.tags} onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))} placeholder="PPWR, Exporters" className={`${inputCls} mt-1`} data-testid="article-input-tags" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-forest-deep/60">Excerpt (shown in lists and Google)</label>
            <textarea rows={2} maxLength={400} value={draft.excerpt} onChange={(e) => setDraft((d) => ({ ...d, excerpt: e.target.value }))} className={`${inputCls} mt-1`} data-testid="article-input-excerpt" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-forest-deep/60">Body</label>
            <p className="mt-1 text-xs text-forest-deep/50">Blank line = new paragraph. Start a line with ## for a heading, with - for a bullet. Wrap text in ** for bold.</p>
            <textarea required rows={14} value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} className={`${inputCls} mt-1 font-mono text-[13px] leading-relaxed`} data-testid="article-input-body" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} className="rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600" data-testid="article-input-status">
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published (live)</option>
            </select>
            <button type="submit" className="rounded-full bg-forest-primary px-6 py-2.5 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich" data-testid="article-save">
              Save article
            </button>
            <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-emerald-950/20 px-5 py-2.5 text-sm font-semibold text-forest-deep transition-colors hover:bg-emerald-900/5" data-testid="article-cancel">
              Cancel
            </button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section className="mt-8" data-testid="articles-manager">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-emerald-700" />
        <h2 className="font-display text-lg font-bold text-forest-deep">Insights articles</h2>
        <span className="rounded-full bg-emerald-900/5 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-800/70">{articles.length} total</span>
        <button onClick={startNew} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-forest-primary px-4 py-2 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich" data-testid="article-new-button">
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>
      <p className="mt-1 text-sm text-forest-deep/60">Live at /insights. Publish only when ready; drafts stay hidden. Every published article joins the sitemap automatically.</p>

      <div className="mt-5 space-y-3">
        {articles.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-950/10 bg-white p-4 shadow-sm" data-testid={`article-row-${a.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${a.status === "published" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>
                  {a.status}
                </span>
                <span className="truncate font-display text-sm font-bold text-forest-deep">{a.title}</span>
              </div>
              <div className="mt-1 font-mono text-xs text-forest-deep/50">/insights/{a.slug}</div>
            </div>
            <button onClick={() => startEdit(a)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-950/15 px-4 py-2 text-sm font-semibold text-forest-deep transition-colors hover:bg-emerald-900/5" data-testid={`article-edit-${a.id}`}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button onClick={() => remove(a.id)} aria-label="Delete article" className="rounded-full border border-red-200 p-2 text-red-600 transition-colors hover:bg-red-50" data-testid={`article-delete-${a.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
